// Direct checklist creation modal (PLAN.md docs/plans/active/PUBLIC_LAUNCH_UX_PLAN.md Task 2.1).
// Skips the List -> Template -> Checklist chain entirely (D-30/§4.2 POST /checklists).

import { LIMITS, type ListKind } from "@checklist/shared";
import { useEffect, useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { ErrorBanner, fieldCls, labelCls } from "./AuthShell.js";
import { ImportPastePanel } from "./ImportPastePanel.js";
import { Button } from "./ui/Button.js";
import { Modal } from "./ui/Modal.js";
import { parseImportTextWithStats } from "../lib/importParser.js";
import { humanizeApiError } from "../lib/apiErrors.js";
import { ApiError, createChecklist } from "../lib/api.js";

function kindToggleCls(active: boolean): string {
  return [
    "h-9 rounded-lg border px-3 text-sm font-semibold transition",
    active
      ? "border-brand-600 bg-brand-600 text-white"
      : "border-line bg-white text-ink hover:bg-slate-50",
  ].join(" ");
}

export function NewChecklistModal({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [kind, setKind] = useState<ListKind>("packing");
  const [itemsText, setItemsText] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setName("");
    setKind("packing");
    setItemsText("");
    setError(null);
    setSaving(false);
  }, [open]);

  const { items: parsedItems, duplicates } = parseImportTextWithStats(itemsText);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    const trimmedName = name.trim();
    if (!trimmedName) {
      setError("Enter a name.");
      return;
    }
    if (parsedItems.length > LIMITS.listItemsMax) {
      setError(`Checklists can start with at most ${LIMITS.listItemsMax} items.`);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const chk = await createChecklist({
        name: trimmedName,
        kind,
        items: parsedItems,
      });
      onClose();
      navigate(`/checklists/${chk.id}`);
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) return;
      setError(humanizeApiError(err, "Failed to create checklist"));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal open={open} onClose={onClose} labelledBy="new-checklist-title">
      <h2 id="new-checklist-title" className="text-lg font-semibold text-ink">
        New checklist
      </h2>
      <p className="mt-1 text-sm text-muted">
        Name it and go — you can turn it into a reusable template later.
      </p>

      <form className="mt-4 space-y-4" onSubmit={(e) => void onSubmit(e)}>
        <ErrorBanner message={error} />

        <div>
          <label htmlFor="newChecklistName" className={labelCls}>
            Name
          </label>
          <input
            id="newChecklistName"
            className={fieldCls}
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Cabo, July 2026"
            maxLength={LIMITS.nameMax}
            autoFocus
          />
        </div>

        <div>
          <p className={labelCls} id="newChecklistKindLabel">
            Kind
          </p>
          <div
            className="mt-1 flex flex-wrap gap-2"
            role="group"
            aria-labelledby="newChecklistKindLabel"
          >
            <button
              type="button"
              className={kindToggleCls(kind === "packing")}
              aria-pressed={kind === "packing"}
              onClick={() => setKind("packing")}
            >
              Packing
            </button>
            <button
              type="button"
              className={kindToggleCls(kind === "todo")}
              aria-pressed={kind === "todo"}
              onClick={() => setKind("todo")}
            >
              To-do
            </button>
          </div>
        </div>

        <div>
          <label htmlFor="newChecklistItems" className={labelCls}>
            Items <span className="font-normal text-muted">(optional)</span>
          </label>
          <ImportPastePanel
            id="newChecklistItems"
            value={itemsText}
            onChange={setItemsText}
            preview={parsedItems}
            duplicates={duplicates}
            remaining={LIMITS.checklistItemsMax}
          />
        </div>

        <Button type="submit" fullWidth disabled={saving}>
          {saving ? "Creating…" : "Create checklist"}
        </Button>
      </form>
    </Modal>
  );
}
